import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  TextField,
  Autocomplete,
  CircularProgress,
  Chip,
  AvatarGroup
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Search as SearchIcon,
  EmojiEvents as TrophyIcon,
  Casino as DeckIcon
} from '@mui/icons-material';
import SectionTitle from '../components/SectionTitle';
import mockTournaments from '../mockData/tournaments';

// Mock data for autocomplete
const mockUsers = [
  { id: 101, username: "AnaLegend", avatarUrl: "https://mui.com/static/images/avatar/1.jpg", mainDeck: "Inkas" },
  { id: 102, username: "CarlosGamer", avatarUrl: "https://mui.com/static/images/avatar/2.jpg", mainDeck: "Egipcios" },
  { id: 103, username: "ElenaProPlayer", avatarUrl: "https://mui.com/static/images/avatar/3.jpg", mainDeck: "Nórdicos" },
  { id: 104, username: "DavidCardMaster", avatarUrl: "https://mui.com/static/images/avatar/4.jpg", mainDeck: "Griegos" },
  { id: 105, username: "SofíaChampion", avatarUrl: "https://mui.com/static/images/avatar/5.jpg", mainDeck: "Japoneses" }
];

const TournamentParticipantsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // For demo purposes
  const isTournamentAdmin = true;

  useEffect(() => {
    // Fetch tournament data from mock
    const loadData = () => {
      try {
        setLoading(true);
        console.log("Buscando torneo con ID:", id);
        console.log("Torneos disponibles:", mockTournaments);
        
        // Si el ID es una cadena que comienza con 't', elimina el 't' para la comparación numérica
        let searchId = id;
        if (typeof id === 'string' && id.startsWith('t')) {
          searchId = id; // Mantiene el formato original de ID para comparaciones exactas
        }
        
        const foundTournament = mockTournaments.find(t => t.id === searchId);
        console.log("Torneo encontrado:", foundTournament);
        
        if (foundTournament) {
          setTournament(foundTournament);
          
          // Set participants with position property
          const participantsWithPosition = foundTournament.participants.map((p, index) => ({
            ...p,
            position: index + 1
          }));
          setParticipants(participantsWithPosition);
        } else {
          // Si no se encuentra el torneo, usar datos de prueba
          console.log("Torneo no encontrado, usando datos de prueba");
          setError('Torneo no encontrado. Usando datos de prueba.');
          
          const mockTournament = {
            id: id,
            name: 'Torneo de Ejemplo',
            status: 'open',
            type: 'single_elimination'
          };
          
          setTournament(mockTournament);
          
          // Crear participantes de ejemplo
          const mockParticipants = [
            { id: 1, username: "Jugador1", avatarUrl: "https://mui.com/static/images/avatar/1.jpg", mainDeck: "Atlantes", position: 1 },
            { id: 2, username: "Jugador2", avatarUrl: "https://mui.com/static/images/avatar/2.jpg", mainDeck: "Egipcios", position: 2 }
          ];
          
          setParticipants(mockParticipants);
        }
      } catch (err) {
        console.error("Error al cargar los datos:", err);
        setError("Error al cargar los datos del torneo. " + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      // Here we would make an API call to save participants
      await new Promise(resolve => setTimeout(resolve, 1000)); // Mock API call
      navigate(`/tournaments/${id}`);
    } catch (error) {
      setError('Error al guardar los cambios');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddParticipant = (participant) => {
    if (!participant) return;
    
    // Check if user is already in the tournament
    if (participants.some(p => p.id === participant.id)) {
      setError('Este usuario ya está en el torneo');
      return;
    }
    
    const newPosition = participants.length > 0 
      ? Math.max(...participants.map(p => p.position)) + 1 
      : 1;
    
    setParticipants([
      ...participants, 
      { 
        ...participant, 
        position: newPosition 
      }
    ]);
    
    setSearchInput('');
  };

  const handleRemoveParticipant = (participantId) => {
    setParticipants(participants.filter(p => p.id !== participantId));
  };

  const filteredOptions = mockUsers.filter(user => 
    !participants.some(p => p.id === user.id) &&
    user.username.toLowerCase().includes(searchInput.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tournament && !loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '80vh',
        gap: 2
      }}>
        <Alert severity="error">No se pudo encontrar el torneo</Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/tournaments')}
          startIcon={<ArrowBackIcon />}
        >
          Volver a Torneos
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '90%',
      maxWidth: '600px',
      mx: 'auto',
      bgcolor: 'transparent'
    }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{ 
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton 
                onClick={() => navigate(`/tournaments/${id}`)}
                sx={{ 
                  width: 36,
                  height: 36,
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '&:hover': { 
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Participantes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tournament.name}
                </Typography>
              </Box>
            </Box>
            {isTournamentAdmin && tournament.status === 'open' && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={submitting}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  bgcolor: '#c89e38',
                  '&:hover': {
                    bgcolor: '#b38d2d'
                  }
                }}
              >
                Guardar
              </Button>
            )}
          </Box>

          {/* Tournament Info Summary */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mt: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={tournament.type === 'single_elimination' ? 'Eliminación Simple' : 
                       tournament.type === 'double_elimination' ? 'Doble Eliminación' : 
                       'Todos contra Todos'}
                size="small"
                sx={{ 
                  borderRadius: 1,
                  bgcolor: 'rgba(200, 158, 56, 0.1)',
                  color: '#c89e38',
                  border: '1px solid rgba(200, 158, 56, 0.3)'
                }}
              />
              <Chip 
                label={tournament.status === 'open' ? 'Abierto' : 
                       tournament.status === 'in_progress' ? 'En Progreso' : 
                       'Finalizado'}
                size="small"
                color={tournament.status === 'open' ? 'success' : 
                       tournament.status === 'in_progress' ? 'warning' : 
                       'default'}
                sx={{ borderRadius: 1 }}
              />
            </Box>
            <AvatarGroup 
              max={5}
              sx={{ 
                '& .MuiAvatar-root': {
                  width: 28,
                  height: 28,
                  fontSize: '0.75rem'
                }
              }}
            >
              {participants.slice(0, 5).map((participant) => (
                <Tooltip key={participant.id} title={participant.username}>
                  <Avatar 
                    src={participant.avatarUrl} 
                    alt={participant.username}
                  >
                    {participant.username[0]}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          </Box>
        </Paper>

        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ 
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Add Participant Section - Only visible for admins and if tournament is open */}
        {isTournamentAdmin && tournament.status === 'open' && (
          <SectionTitle 
            title="Añadir Participante"
          >
            <Autocomplete
              id="add-participant"
              options={filteredOptions}
              getOptionLabel={(option) => option.username}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar usuario"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon 
                        color="action" 
                        sx={{ ml: 1, mr: 0.5, fontSize: '1.2rem' }} 
                      />
                    ),
                    endAdornment: (
                      <>
                        {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main'
                      }
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.5 }}>
                    <Avatar 
                      src={option.avatarUrl} 
                      alt={option.username}
                      sx={{ width: 32, height: 32 }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{option.username}</Typography>
                      {option.mainDeck && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DeckIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {option.mainDeck}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </li>
              )}
              inputValue={searchInput}
              onInputChange={(event, newInputValue) => {
                setSearchInput(newInputValue);
              }}
              onChange={(event, newValue) => {
                handleAddParticipant(newValue);
              }}
              noOptionsText="No se encontraron usuarios"
              loadingText="Buscando..."
              sx={{ width: '100%' }}
            />
          </SectionTitle>
        )}

        {/* Participants Table */}
        <SectionTitle 
          title="Participantes Actuales"
          subtitle={`${participants.length} jugadores inscritos`}
        >
          {participants.length > 0 ? (
            <TableContainer sx={{ 
              borderRadius: 1,
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
                '&:hover': {
                  background: '#555',
                },
              }, 
            }}>
              <Table aria-label="tabla de participantes" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', color: '#c89e38', fontSize: '0.8rem' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#c89e38', fontSize: '0.8rem' }}>Jugador</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#c89e38', fontSize: '0.8rem' }}>Mazo</TableCell>
                    {isTournamentAdmin && tournament.status === 'open' && (
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c89e38', fontSize: '0.8rem' }}>Acciones</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants
                    .sort((a, b) => a.position - b.position)
                    .map((participant) => (
                      <TableRow
                        key={participant.id}
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          transition: 'background-color 0.3s ease'
                        }}
                      >
                        <TableCell component="th" scope="row" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                          {participant.position}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar 
                              src={participant.avatarUrl} 
                              alt={participant.username}
                              sx={{ 
                                width: 32, 
                                height: 32,
                                border: '1px solid rgba(200, 158, 56, 0.3)'
                              }}
                            />
                            <Typography variant="body2">
                              {participant.username}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {participant.mainDeck ? 
                            <Chip 
                              icon={<DeckIcon sx={{ fontSize: '1rem' }} />}
                              label={participant.mainDeck} 
                              size="small"
                              variant="outlined"
                              sx={{ 
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: 24
                              }}
                            /> : 
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          }
                        </TableCell>
                        {isTournamentAdmin && tournament.status === 'open' && (
                          <TableCell align="right">
                            <Tooltip title="Eliminar participante">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveParticipant(participant.id)}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    bgcolor: 'error.light',
                                    color: 'error.dark'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              py: 4,
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 1
            }}>
              <TrophyIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography color="text.secondary">
                No hay participantes registrados
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                sx={{
                  mt: 1,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.85rem'
                }}
                onClick={() => handleAddParticipant(mockUsers[0])}
              >
                Añadir participante
              </Button>
            </Box>
          )}
        </SectionTitle>

        {/* Bottom actions */}
        {isTournamentAdmin && tournament.status === 'open' && participants.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={submitting}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.85rem',
                bgcolor: '#c89e38',
                '&:hover': {
                  bgcolor: '#b38d2d'
                }
              }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Guardar Cambios'
              )}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default TournamentParticipantsPage; 